const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAccessToken = () => localStorage.getItem('accessToken');

const buildPropertyFormData = (propertyData) => {
  const formData = new FormData();

  Object.entries(propertyData).forEach(([key, value]) => {
    if (key === 'images') {
      formData.append('existingImages', JSON.stringify(value || []));
      return;
    }

    if (key === 'amenities') {
      formData.append('amenities', JSON.stringify(value || []));
      return;
    }

    if (key === 'newImageFiles') {
      (value || []).forEach((file) => {
        formData.append('images', file);
      });
      return;
    }

    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  return formData;
};

export const api = {
  async authenticatedFetch(path, options = {}, retryOnAuthFailure = true) {
    const token = getAccessToken();
    const headers = {
      ...(options.headers || {})
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
      credentials: 'include'
    });

    if ((response.status === 401 || response.status === 403) && retryOnAuthFailure) {
      const refreshed = await this.refreshToken();

      if (refreshed) {
        return this.authenticatedFetch(path, options, false);
      }
    }

    return response;
  },

  async getUser() {
    const token = getAccessToken();
    if (!token) return null;
    
    const response = await this.authenticatedFetch('/auth/user');
    
    if (!response.ok) {
      return null;
    }

    return response.json();
  },

  async refreshToken() {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) return false;
      
      const data = await response.json();
      localStorage.setItem('accessToken', data.accessToken);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  },

  async logout() {
    const response = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    localStorage.removeItem('accessToken');
    return response.ok;
  },

  async updateProfile(profileData) {
    const response = await this.authenticatedFetch('/auth/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(profileData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }
    return response.json();
  },

  async getUserById(userId) {
    const response = await fetch(`${API_URL}/api/users/${userId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }
    return response.json();
  },

  getUserListings: async () => {
    const response = await api.authenticatedFetch('/api/properties/user/listings');
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch user listings');
    }
    
    return response.json();
  },

  async getAnalytics() {
    const response = await this.authenticatedFetch('/api/analytics');
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch analytics');
    }
    return response.json();
  },

  async recordContactView(propertyId) {
    try {
      await fetch(`${API_URL}/api/analytics/contact-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ property_id: propertyId })
      });
    } catch (error) {
      console.error('Failed to record contact view:', error);
    }
  },

  async getProperties(filters = {}) {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_URL}/api/properties?${params}`);
    const data = await response.json();
    // Backend returns a plain array by default, or { properties, pagination } when page/limit are passed.
    return Array.isArray(data) ? data : (data.properties || []);
  },

  async getSEOProperties(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await fetch(`${API_URL}/api/seo/properties?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to fetch SEO properties');
    }
    return response.json();
  },

  async getSEORelatedSearches(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await fetch(`${API_URL}/api/seo/related-searches?${params.toString()}`);
    if (!response.ok) {
      return { suggestions: [] };
    }
    return response.json();
  },

  async getSEOSitemap() {
    const response = await fetch(`${API_URL}/api/seo/sitemap`);
    if (!response.ok) {
      throw new Error('Failed to fetch sitemap data');
    }
    return response.text();
  },

  async getProperty(id) {
    const response = await fetch(`${API_URL}/api/properties/${id}`);
    return response.json();
  },

  async getPropertyById(id) {
    return this.getProperty(id);
  },

  uploadWithProgress(path, method, formData, onProgress) {
    const sendWithToken = (token) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, `${API_URL}${path}`, true);
      xhr.withCredentials = true;
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        let data = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = null;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
          return;
        }

        if (xhr.status === 401 || xhr.status === 403) {
          const error = new Error('AUTH_REQUIRED');
          error.status = xhr.status;
          reject(error);
          return;
        }

        const message = (data && (data.details || data.error)) || `Server error: ${xhr.status}`;
        reject(new Error(message));
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(formData);
    });

    return sendWithToken(getAccessToken()).catch(async (error) => {
      if (error.status === 401 || error.status === 403) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          return sendWithToken(getAccessToken());
        }
      }
      throw error;
    });
  },

  async createProperty(propertyData, onProgress) {
    const formData = buildPropertyFormData(propertyData);
    if (onProgress) {
      return this.uploadWithProgress('/api/properties', 'POST', formData, onProgress);
    }

    const response = await this.authenticatedFetch('/api/properties', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.details || errorData.error || 'Failed to create property');
    }

    return response.json();
  },

  async updateProperty(id, propertyData, onProgress) {
    const formData = buildPropertyFormData(propertyData);
    if (onProgress) {
      return this.uploadWithProgress(`/api/properties/${id}`, 'PUT', formData, onProgress);
    }

    const response = await this.authenticatedFetch(`/api/properties/${id}`, {
      method: 'PUT',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.details || errorData.error || 'Failed to update property');
    }

    return response.json();
  },

  async deleteProperty(id) {
    const response = await this.authenticatedFetch(`/api/properties/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to delete property');
    }

    return response.json();
  },

  getGoogleAuthUrl() {
    return `${API_URL}/auth/google`;
  },

  async submitReview(ownerId, reviewData) {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/reviews`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({ owner_id: ownerId, ...reviewData })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Review submission failed:', response.status, errorData);
      throw new Error(errorData.error || 'Failed to submit review');
    }
    return response.json();
  },

  async submitReport(ownerId, reportData) {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({ reported_user_id: ownerId, ...reportData })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Report submission failed:', response.status, errorData);
      throw new Error(errorData.error || 'Failed to submit report');
    }
    return response.json();
  },

  async getOwnerReviews(ownerId) {
    const response = await fetch(`${API_URL}/api/reviews/${ownerId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      return [];
    }
    return response.json();
  },

  async getOwnerListings(ownerId) {
    const response = await fetch(`${API_URL}/api/properties/owner/${ownerId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      return [];
    }
    return response.json();
  },

  async getChats() {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/chats`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch chats');
    }
    return response.json();
  },

  async getChatMessages(chatId) {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/chats/${chatId}/messages`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch messages');
    }
    return response.json();
  },

  async sendMessage(chatId, message) {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/chats/${chatId}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({ message })
    });
    if (!response.ok) {
      throw new Error('Failed to send message');
    }
    return response.json();
  },

  async createChat(propertyId, receiverId) {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/api/chats/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({ property_id: propertyId, receiver_id: receiverId })
    });
    if (!response.ok) {
      throw new Error('Failed to create chat');
    }
    return response.json();
  }
};

export default api;
